import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {
  OrderDetailsSection_CoinsEarning,
  OrderDetailsSection_PriceRow,
  OrderDetailsSection_Total,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/order_success_page/v1/sections.types';
import {useFormatCurrency} from '@aboutyou/i18n';
import {
  COLORS,
  FONT_SIZES,
  Text,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {PriceSummaryRow} from '../../../features/PriceSummaryRow/PriceSummaryRow';
import {BasketTotalCoinsRow} from '../../Basket/BasketTotal/Components/BasketTotalCoinsRow';

export const PriceSection: React.FC<{
  priceRows: OrderDetailsSection_PriceRow[];
  total: OrderDetailsSection_Total;
  legalText: string;
  coinsEarning?: OrderDetailsSection_CoinsEarning;
}> = ({priceRows, total, legalText, coinsEarning}) => {
  const {isActive: isCoinsActive} = useIsFeatureActive('ff_coins_rollout');
  const {isActive: isSecondaryPriceLabelActive} = useIsFeatureActive(
    'secondary_price_label_BG',
  );

  const formatCurrency = useFormatCurrency();

  return (
    <PriceSectionWrapper>
      <PriceSectionInner>
        <PriceSummaryRowWrapper data-testid="ospPriceRowContainer">
          {priceRows.map((row, i) => (
            <PriceSummaryRow key={i} {...row} />
          ))}
        </PriceSummaryRowWrapper>
        <PriceTotalWrapper>
          <TotalText weight="mediumHeavy" size="px20">
            {total.label}
          </TotalText>
          <TotalValueWrapper>
            <TotalText
              weight="mediumHeavy"
              size="px20"
              data-testid="ospFinalPrice"
            >
              {formatCurrency(total.amount.amount, total.amount.currencyCode)}
              {total.secondaryPriceLabel && isSecondaryPriceLabelActive && (
                <SecondaryPriceLabel>
                  {total.secondaryPriceLabel}
                </SecondaryPriceLabel>
              )}
            </TotalText>
            {total.taxInformation && (
              <TaxInformationText size="px14" color="Grey1">
                {total.taxInformation}
              </TaxInformationText>
            )}
          </TotalValueWrapper>
        </PriceTotalWrapper>
        {isCoinsActive && coinsEarning && (
          <CoinsEarning
            title={coinsEarning.title}
            amount={coinsEarning.amount}
          />
        )}
      </PriceSectionInner>
      <LegalTextWrapper>
        <Text color="Grey0" size="px14">
          {legalText}
        </Text>
      </LegalTextWrapper>
    </PriceSectionWrapper>
  );
};

const SecondaryPriceLabel = styled.div`
  color: ${COLORS['Text.Grey']};
  margin-left: 0.25rem;
`;

const LegalTextWrapper = styled.div`
  padding: 1rem 1rem 2rem;
  text-align: center;

  ${tabletAndUpLinaria(`
    text-align: unset;
    padding: 0 0 1.5rem 0;
  `)}
`;

const CoinsEarning = styled(BasketTotalCoinsRow)`
  margin-top: 16px;

  ${tabletAndUpLinaria(`
    margin-top: 8px;
  `)}
`;

const TotalText = styled(Text)`
  line-height: 1.5rem;
  display: flex;
`;

const TaxInformationText = styled(Text)`
  line-height: 1.25rem;
`;

const TotalValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: right;
`;

const PriceSummaryRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.25rem;
  gap: 0.3125rem;
  font-size: ${FONT_SIZES.px14};
`;

const PriceTotalWrapper = styled.div`
  margin-top: 0.5rem;
  display: flex;
  justify-content: space-between;

  ${tabletAndUpLinaria(`
    border-top: 1px solid ${COLORS.Grey3};
    margin-top: 1rem;
    padding-top: 1rem;
  `)}
`;

const PriceSectionWrapper = styled.div`
  padding: 0 1rem;

  ${tabletAndUpLinaria(`
    padding: 0;
  `)}
`;

const PriceSectionInner = styled.div`
  border-top: 1px solid ${COLORS.Grey3};
  border-bottom: 1px solid ${COLORS.Grey3};

  padding: 1rem 0 1.5rem 0;
  ${tabletAndUpLinaria(`
    border-bottom: unset;
  `)}
`;
